import { styleFor, isPlanned } from "@/lib/dental";

// One tooth: a crown box split into five regions — four trapezoids around a
// central table — plus the FDI number.
//
// Geometry of a 40x40 crown, centre table from 12,12 to 28,28.
const REGION_PATHS = {
  top:    "M0,0 L40,0 L28,12 L12,12 Z",
  bottom: "M0,40 L40,40 L28,28 L12,28 Z",
  left:   "M0,0 L12,12 L12,28 L0,40 Z",
  right:  "M40,0 L28,12 L28,28 L40,40 Z",
};

/**
 * Which anatomical surface sits on which side of the drawn cell.
 *
 * Mesial means "toward the midline", so it flips with the quadrant: quadrants 1
 * and 4 (patient's right) are drawn 18→11 left-to-right, putting mesial on the
 * RIGHT of the cell; quadrants 2 and 3 are drawn 21→28, putting it on the LEFT.
 * Buccal faces outward — up for the upper arch, down for the lower. Getting this
 * wrong would mislabel every filling on half the mouth.
 */
export function surfaceSides(tooth) {
  // Supernumerary teeth (ISO 10394) use letters: A upper-right, B upper-left,
  // C lower-left, D lower-right. AB / DC are the midline cases.
  const letter = tooth[0];
  const isSuper = ["A", "B", "C", "D"].includes(letter);
  const midline = tooth === "AB" || tooth === "DC";

  let isUpper, mesialOnRight, isAnterior;
  if (isSuper) {
    isUpper = tooth === "AB" || letter === "A" || letter === "B";
    if (tooth === "DC") isUpper = false;
    mesialOnRight = letter === "A" || letter === "D";
    isAnterior = midline || Number(tooth[1]) <= 3;
  } else {
    const q = Number(letter);
    isUpper = q === 1 || q === 2 || q === 5 || q === 6;
    mesialOnRight = q === 1 || q === 4 || q === 5 || q === 8;
    isAnterior = Number(tooth[1]) <= 3;
  }

  return {
    top: isUpper ? "B" : "L",
    bottom: isUpper ? "L" : "B",
    right: mesialOnRight ? "M" : "D",
    left: mesialOnRight ? "D" : "M",
    centre: isAnterior ? "I" : "O",
  };
}

export default function ToothSvg({
  tooth,
  entry,
  selected = false,
  dirty = false,
  size = 44,
  onSelect,
  onSurfaceTap,
}) {
  const sides = surfaceSides(tooth);
  const surfaces = entry?.surfaces || {};
  const whole = entry?.whole || null;
  const wholeStyle = whole ? styleFor(whole.finding_type) : null;
  const gone = whole && ["missing", "extracted"].includes(whole.finding_type);

  const paint = (surfaceLetter) => {
    const mark = surfaces[surfaceLetter];
    if (!mark) return { fill: "#ffffff", stroke: "#cbd5e1", planned: false };
    const st = styleFor(mark.finding_type);
    return { fill: st.fill, stroke: st.stroke, planned: isPlanned(mark) };
  };

  const region = (key, d) => {
    const letter = sides[key];
    const p = paint(letter);
    return (
      <path
        key={key}
        d={d}
        fill={p.planned ? "#ffffff" : p.fill}
        stroke={p.planned ? p.fill : p.stroke}
        strokeWidth={p.planned ? 2.5 : 1}
        strokeDasharray={p.planned ? "3 2" : undefined}
        onPointerUp={onSurfaceTap ? (e) => { e.stopPropagation(); onSurfaceTap(tooth, letter); } : undefined}
        style={{ cursor: onSurfaceTap ? "pointer" : "inherit" }}
      />
    );
  };

  const centre = paint(sides.centre);

  return (
    <div
      className="flex flex-col items-center select-none"
      style={{ width: size }}
      data-testid={`tooth-${tooth}`}
    >
      <svg
        viewBox="0 0 40 40"
        width={size}
        height={size}
        // onPointerUp, not onClick: tablets fire ghost clicks and add a delay.
        onPointerUp={() => onSelect && onSelect(tooth)}
        style={{ touchAction: "manipulation", cursor: "pointer" }}
        role="button"
        aria-label={`Tooth ${tooth}`}
      >
        <rect
          x="0" y="0" width="40" height="40"
          fill={selected ? "#dbeafe" : "#ffffff"}
          stroke={selected ? "#2563eb" : "#cbd5e1"}
          strokeWidth={selected ? 3 : 1}
        />

        {gone ? (
          // A missing tooth is struck through rather than tinted — unmistakable
          // even in a monochrome printout.
          <g>
            <line x1="4" y1="4" x2="36" y2="36" stroke="#64748b" strokeWidth="3" />
            <line x1="36" y1="4" x2="4" y2="36" stroke="#64748b" strokeWidth="3" />
          </g>
        ) : (
          <>
            {region("top", REGION_PATHS.top)}
            {region("bottom", REGION_PATHS.bottom)}
            {region("left", REGION_PATHS.left)}
            {region("right", REGION_PATHS.right)}
            <rect
              x="12" y="12" width="16" height="16"
              fill={centre.planned ? "#ffffff" : centre.fill}
              stroke={centre.planned ? centre.fill : centre.stroke}
              strokeWidth={centre.planned ? 2.5 : 1}
              strokeDasharray={centre.planned ? "3 2" : undefined}
              onPointerUp={onSurfaceTap ? (e) => { e.stopPropagation(); onSurfaceTap(tooth, sides.centre); } : undefined}
              style={{ cursor: onSurfaceTap ? "pointer" : "inherit" }}
            />
            {/* Whole-tooth findings that aren't "gone" (crown, RCT, implant)
                sit as a glyph over the middle so surface work stays visible. */}
            {whole && !gone ? (
              <g>
                <circle cx="20" cy="20" r="9" fill={wholeStyle.fill} stroke={wholeStyle.stroke} strokeWidth="1.5" />
                <text
                  x="20" y="24" textAnchor="middle"
                  fontSize="10" fontWeight="700" fill={wholeStyle.text}
                  style={{ pointerEvents: "none" }}
                >
                  {wholeStyle.glyph}
                </text>
              </g>
            ) : null}
          </>
        )}
      </svg>

      <div className="flex items-center gap-1 mt-0.5">
        <span className={`text-[10px] mono ${selected ? "font-bold text-blue-700" : "text-slate-500"}`}>
          {tooth}
        </span>
        {dirty ? <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Unsaved" /> : null}
      </div>
    </div>
  );
}
